// src/Pages/SessionReportDetail/Components/attendance-kpis-cards.jsx
import PropTypes from 'prop-types';
import { Box, Card, Skeleton, Typography } from '@mui/material';
import {
  PercentOutlined,
  CheckCircleOutlined,
  CancelOutlined,
  AssignmentLateOutlined,
  HourglassEmptyOutlined,
} from '@mui/icons-material';
import { colors } from 'Constants/Colors';
import useTranslation from 'Hooks/useTranslation';

const styles = {
  grid: {
    display: 'grid',
    gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(5, 1fr)' },
    gap: 2,
    mb: 2.5,
  },
  card: {
    p: 2.25,
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    borderRadius: 2,
    boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
    border: `1px solid ${colors.GRAY_HR}`,
  },
  value: { fontSize: 22, fontWeight: 700, color: colors.BLACK, lineHeight: 1.1 },
  label: { fontSize: 12.5, color: colors.GRAY_DARK, mt: 0.5 },
};

const iconWrapStyle = (tint) => ({
  width: 42,
  height: 42,
  borderRadius: 1.25,
  display: 'grid',
  placeItems: 'center',
  flexShrink: 0,
  background: `${tint}1A`,
  color: tint,
});

/**
 * Read-only KPI cards for a single session. Mirrors SessionsReportKpis layout.
 * Numbers come from sessionsApi.getSessionStats — frontend renders, never aggregates.
 */
export default function AttendanceKpisCards({ stats, loading }) {
  const { t2 } = useTranslation('SessionsReport');

  const items = [
    {
      key: 'attendance',
      tint: colors.GREEN,
      icon: <PercentOutlined />,
      value: `${stats?.attendancePercentage ?? 0}%`,
      label: t2('detail.kpis.attendancePercentage'),
    },
    {
      key: 'present',
      tint: colors.GREEN_LEAF || colors.GREEN,
      icon: <CheckCircleOutlined />,
      value: stats?.counters?.present ?? 0,
      label: t2('detail.kpis.present'),
    },
    {
      key: 'absent',
      tint: colors.RED || '#D33',
      icon: <CancelOutlined />,
      value: stats?.counters?.absent ?? 0,
      label: t2('detail.kpis.absent'),
    },
    {
      key: 'justified',
      tint: colors.ORANGE || '#F59E0B',
      icon: <AssignmentLateOutlined />,
      value: stats?.counters?.justified ?? 0,
      label: t2('detail.kpis.justified'),
    },
    {
      key: 'pending',
      tint: colors.GRAY_DARK,
      icon: <HourglassEmptyOutlined />,
      value: stats?.counters?.pending ?? 0,
      label: t2('detail.kpis.pending'),
    },
  ];

  return (
    <Box sx={styles.grid}>
      {items.map((it) => (
        <Card key={it.key} sx={styles.card} elevation={0}>
          <Box sx={iconWrapStyle(it.tint)}>{it.icon}</Box>
          <Box>
            {loading && !stats ? (
              <Skeleton variant="text" width={60} height={28} />
            ) : (
              <Typography sx={styles.value}>{it.value}</Typography>
            )}
            <Typography sx={styles.label}>{it.label}</Typography>
          </Box>
        </Card>
      ))}
    </Box>
  );
}

AttendanceKpisCards.propTypes = {
  stats: PropTypes.shape({
    attendancePercentage: PropTypes.number,
    counters: PropTypes.shape({
      present: PropTypes.number,
      absent: PropTypes.number,
      justified: PropTypes.number,
      pending: PropTypes.number,
    }),
  }),
  loading: PropTypes.bool,
};

AttendanceKpisCards.defaultProps = {
  stats: null,
  loading: false,
};
